// v0.4.85 — Single translator for ALL operator-facing error
// copy. One module, three consumers:
//
//   1. `getRuleCopy(ruleName, fallbackDescription)` → returns
//      `{ headline, fix, hint }` for reconciliation_failure
//      records. Consumed by `<ActionRequiredBanner />` for the
//      bold headline + fix line, and by `<CountryReturnTabs />`
//      for the per-chip `↳` breach-hint underneath the chip row.
//
//   2. `getPageBuildErrorCopy(code, message)` → returns
//      `{ headline, fix, technicalDetails, actions }` for
//      top-level page-build errors (the 409s thrown when a
//      country-return endpoint refuses to build the form).
//      Returns `null` when the code isn't known — caller falls
//      back to the raw backend message.
//
// Adding a new rule or page-build error is a single-file change.
// Pattern signed off 2026-06-19 (option c).

// ── Per-rule reconciliation_failure copy ─────────────────────

import { RULE_COPY_FORMS } from "./ruleCopyForms";   // A2-234 G8

const RULE_COPY = {
  ...RULE_COPY_FORMS,
  // v0.6.13 (A1-51) — purchase rows fill the input-VAT boxes. These four
  // are NOTES (never block): they say what the pre-filled figure assumed.
  purchases_vat_charged_instead_of_reverse_charge: {
    headline: "A supplier from abroad charged VAT on a service you reverse-charge.",
    fix:
      "Ask the supplier for a corrected invoice without VAT. The note on the "
      + "return says how the VAT on it was treated.",
  },
  purchases_invoice_in_two_uploads: {
    headline: "The same supplier invoice is in more than one upload.",
    fix:
      "If it is the same invoice, remove it from one of the files and upload "
      + "again; otherwise its VAT is counted twice.",
  },
  purchases_from_a_duplicate_upload: {
    headline: "Purchases from the same file are counted twice.",
    fix:
      "Two copies of the same upload are selected in this calculation. Run it "
      + "again with only one of them, or the reclaimable VAT is doubled.",
  },
  purchases_without_expense_type: {
    headline: "Some purchases have no expense type yet.",
    fix:
      "In the purchases list below, pick a type for each row that is not a "
      + "general business cost (for example a car, fuel, a meal, a hotel or "
      + "entertainment). Until then they count as fully reclaimable.",
  },
  purchases_counted_at_the_safe_share: {
    headline:
      "Some purchases were counted at the lowest reclaimable share.",
    fix:
      "In the purchases list below, answer the question beside each row "
      + "that has one (for example, whether a car is also used privately). "
      + "The reclaimable VAT is worked out again from your answers.",
    hint: "Purchase questions are waiting for an answer.",
  },
  purchases_maybe_domestic_reverse_charge: {
    headline: "Some domestic purchases show no VAT: check for the domestic reverse charge.",
    fix:
      "If any is under the domestic reverse charge (construction, mobile "
      + "phones, scrap metal, energy…), you owe the VAT: enter it and its "
      + "deduction in the boxes named.",
    hint: "Domestic purchases without VAT: possible reverse charge.",
  },
  purchases_with_foreign_vat: {
    headline: "Another country's VAT was left out of this return.",
    fix:
      "VAT charged by a supplier in another country cannot be reclaimed "
      + "here. Claim it from that country through its VAT refund procedure.",
    hint: "Foreign VAT on purchases is not reclaimable on this return.",
  },
  purchases_under_reverse_charge_not_computed: {
    headline: "Some purchases from abroad without VAT could not be placed.",
    fix:
      "This form has no box for them here, or their rate category has no "
      + "rate. Enter the VAT you owe on them, and its deduction, yourself.",
    hint: "Some reverse-charge purchases need entering by hand.",
  },
  purchases_taxed_where_performed: {
    headline: "Some purchases are taxed where they took place, not here.",
    fix:
      "Hotels, restaurants, events, transport, property work or vehicle "
      + "hire abroad: nothing is due here. Claim any VAT from that country.",
    hint: "Purchases taxed in another country.",
  },
  purchases_supplier_should_charge_local_vat: {
    headline: "A supplier from abroad should have charged this country's VAT.",
    fix: "Nothing was claimed. Ask the supplier for an invoice with VAT.",
    hint: "Supplier should have charged local VAT.",
  },
  purchases_imported_not_computed: {
    headline: "Import VAT is not taken from purchase rows.",
    fix:
      "Import VAT is worked out on the customs value on the import "
      + "declaration. Enter it from that document, or from your postponed "
      + "import VAT statement.",
    hint: "Imported goods: enter the import VAT by hand.",
  },
  purchases_vat_not_at_a_rate: {
    headline: "Some purchase VAT does not match this country's rates.",
    fix: "Check those invoices for a typing slip or the wrong rate before claiming.",
    hint: "Purchase VAT that matches no rate.",
  },
  purchases_outside_the_period: {
    headline: "Purchase invoices outside this period were not counted.",
    fix:
      "Earlier ones belong in their own period's return (or a late claim); "
      + "later ones count in the period of their date.",
    hint: "Some purchases fall outside this period.",
  },
  purchases_without_an_invoice_number: {
    headline: "Some purchases with VAT have no invoice number.",
    fix: "Reclaiming VAT needs a valid invoice. Make sure you hold it.",
    hint: "Purchases without an invoice number.",
  },
  purchases_eu_goods_charged_foreign_vat: {
    headline: "An EU supplier charged its own VAT on goods.",
    fix:
      "Goods between VAT-registered businesses in different EU countries "
      + "normally move without VAT. Ask the supplier for a corrected invoice.",
    hint: "EU goods invoiced with the supplier's VAT.",
  },
  purchases_self_assessed_at_standard_rate: {
    headline: "Purchases from abroad were taxed at the standard rate.",
    fix:
      "They had no Rate category. If any is taxed at a reduced rate here, "
      + "set its Rate category in the file and upload again.",
    hint: "Self-assessed purchases assumed at the standard rate.",
  },
  // v0.6.12 (audit finding C2-1) — these two used to fall back to a
  // backend description that said the box was "unverified" and sent the
  // operator to look for it. There is no such box: the buyer declares
  // these supplies. Say that, and say what to do.
  // v0.6.12 (A2-37, operator decision 2026-10-02) — these four are NOTES,
  // not blockers (rendered by <AdvisoryNotices />, never the rose banner):
  // the form has no supplier-side box for the amount, the buyer declares
  // it, and nothing needs doing. Same words in all four countries.
  // v0.6.12 (A1-44 Phase 0) — plausibility questions about the
  // operator's own typed figures. getRuleCopy falls back to the
  // backend's description, which is a paragraph; these give the short
  // headline the amber notice strip is built for.
  piva_sides_agree: {
    headline:
      "Postponed import VAT differs between the output and input sides.",
    fix:
      "Both normally come from the same C79 statement total, so a "
      + "difference is usually a keying slip. Correct if the entity "
      + "cannot reclaim in full. You can still file.",
  },
  drc_sides_agree: {
    headline:
      "Domestic reverse-charge VAT differs between the output and input "
      + "sides.",
    fix:
      "The two normally carry the same figure. Correct if the entity "
      + "cannot reclaim in full. You can still file.",
  },
  box4_within_purchases_rate_ceiling: {
    headline:
      "More VAT reclaimed in Box 4 than the purchases in Box 7 could "
      + "produce.",
    fix:
      "Check both — they are typed in by hand, so one is probably a "
      + "slip. You can still file.",
  },
  received_vat_also_reclaimed: {
    headline:
      "VAT self-assessed on supplies received is larger than the "
      + "voorbelasting being reclaimed.",
    fix:
      "If the entity can reclaim it, it normally belongs in rubriek 5b "
      + "too, and leaving it out overstates the payment. Correct if it "
      + "cannot reclaim in full. You can still file.",
  },
  acquisition_vat_also_deducted: {
    headline:
      "VAT self-assessed on acquisitions is larger than the total being "
      + "deducted.",
    fix:
      "If the entity can reclaim it, it normally belongs on lignes "
      + "19/20/21 too, and leaving it out overstates the payment. "
      + "Correct if it cannot reclaim in full. You can still file.",
  },
  kz066_claims_more_than_kz057_owes: {
    headline:
      "KZ 066 claims more reverse-charge input VAT than KZ 057 "
      + "self-assesses.",
    fix:
      "The two boxes cite the same \u00a719 liability, so they describe one "
      + "amount from two sides \u2014 one of the figures is probably a slip. "
      + "You can still file.",
  },
  kz057_vat_also_reclaimed: {
    headline:
      "Reverse-charge VAT self-assessed in KZ 057 is larger than the "
      + "input VAT reclaimed in KZ 066.",
    fix:
      "If the entity can reclaim it, it normally belongs in KZ 066 too, "
      + "and leaving it out overstates the payment. Correct if it cannot "
      + "reclaim in full. You can still file.",
  },
  self_assessed_vat_also_deducted: {
    headline:
      "VAT self-assessed as buyer (\u0159.3-13) is larger than the entire "
      + "input VAT deduction (\u0159.63).",
    fix:
      "If the entity can reclaim it, it normally belongs in \u0159.63 too, "
      + "and leaving it out overstates the payment. Correct if it cannot "
      + "reclaim in full. You can still file.",
  },
  kz61_within_acquisition_tax: {
    headline:
      "Kz 61 doesn't match the acquisition tax this return states.",
    fix:
      "Larger claims acquisition input VAT the acquisitions never "
      + "produced \u2014 probably a slip. Smaller means VAT self-assessed "
      + "but not reclaimed, correct only if the entity cannot reclaim in "
      + "full. You can still file.",
  },
  kz67_within_s13b_tax: {
    headline:
      "Kz 67 doesn't match the \u00a713b VAT self-assessed on this return.",
    fix:
      "Larger claims \u00a713b input VAT the received supplies never "
      + "produced \u2014 probably a slip. Smaller means VAT self-assessed "
      + "but not reclaimed, correct only if the entity cannot reclaim in "
      + "full. You can still file.",
  },
  vp4_rc_adjustment_also_in_vp5: {
    headline:
      "Reverse-charge IVA self-assessed in VP4 is larger than the whole "
      + "deductible IVA in VP5.",
    fix:
      "If the entity can deduct it, it normally belongs in VP5 too, and "
      + "leaving it out overstates the payment. Correct if it cannot "
      + "deduct in full. You can still file.",
  },
  reverse_charge_vat_also_reclaimed: {
    headline:
      "VAT self-assessed on foreign purchases is larger than the input "
      + "VAT being reclaimed.",
    fix:
      "If the entity can reclaim that VAT it normally belongs in "
      + "Købsmoms too, and leaving it out overstates the payment. "
      + "Correct if it cannot reclaim in full. You can still file.",
  },
  rate_lines_multiply_out: {
    headline:
      "A rate line's VAT doesn't match its base times its rate.",
    fix:
      "Check the three figures on that line — base, rate and VAT are all "
      + "typed in by hand, so one is probably a slip. You can still file.",
    hint:
      "Only the lines where a rate is written next to the figures are "
      + "checked.",
  },
  surcharges_multiply_out: {
    headline:
      "A surcharge line doesn't multiply out at the rate printed beside it.",
    fix:
      "Check the base and the surcharge figure against the rate shown on "
      + "the form. You can still file.",
    hint: "The rate used is the one printed on the form, not a guess.",
  },
  deduction_within_rate_ceiling: {
    headline:
      "More VAT reclaimed on a purchases line than its base could produce.",
    fix:
      "Check the base and the VAT on that line. These lines mix purchases "
      + "taxed at different rates, so no single rate is expected — only "
      + "that the VAT can't exceed the highest rate in force. You can "
      + "still file.",
    hint:
      "The ceiling comes from the rate table for this period, so it "
      + "follows rate changes.",
  },
  deduction_cuota_has_its_base: {
    headline:
      "VAT claimed on a purchases line with no base beside it.",
    fix:
      "Enter the net purchases figure the VAT was calculated from. A "
      + "base with no VAT is fine and isn't flagged \u2014 zero-rated "
      + "and exempt purchases look like that. You can still file.",
    hint:
      "Casilla 28 is the base casilla 29's VAT was worked out on.",
  },
  pt_seller_rc_unverified: {
    headline:
      "Portugal's return has no box for reverse-charge sales — the buyer "
      + "declares them, not you.",
    fix:
      "Nothing to do. These sales are left off this return and shown here "
      + "so nothing is lost. The buyer accounts for the VAT on their own "
      + "Quadro 06-A (campos 97/98).",
    hint:
      "Reverse-charge sales are shown, not filed — the buyer declares them.",
  },
  dk_domestic_rc_unverified: {
    headline:
      "Denmark's return has no box for a foreign seller's reverse-charge "
      + "sales — the buyer declares them, not you. (A Danish seller's go in Rubrik C.)",
    fix:
      "Nothing to do. These sales are left off this return and shown here "
      + "so nothing is lost. The buyer accounts for the VAT in their own "
      + "Rubrik A / Rubrik A-ydelser.",
    hint:
      "Reverse-charge sales are shown, not filed — the buyer declares them.",
  },
  cz_non_established_rc_excluded: {
    headline:
      "Czechia's return has no row for reverse-charge sales by a supplier "
      + "not established there — the buyer declares them, not you.",
    fix:
      "Nothing to do. These sales are left off ř. 25 (which covers domestic "
      + "§ 92a reverse charge only) and shown here so nothing is lost. The "
      + "buyer accounts for the VAT.",
    hint:
      "Non-established reverse-charge sales are shown, not filed — the buyer declares them.",
  },
  // v0.6.12 (A2-109) — Portugal's refund conditions, CIVA art. 22.º.
  pt_refund_minimums_art22: {
    headline:
      "A refund is requested on a credit of €3,000 or less — Portugal "
      + "allows that only in two cases.",
    fix:
      "Confirm one applies: the credit is over €250 and has stood for 12 "
      + "months, or the business is ceasing / changing regime and the "
      + "refund is at least €25. Otherwise set campo 95 to 0 and carry "
      + "the credit forward. You can still file.",
    hint: "Refund below €3,000 credit — check the CIVA art. 22.º conditions.",
  },
  // v0.6.12 (A2-108) — was hu_non_established_rc_excluded, which said the
  // form had no row for these sales. It does: row 35, "Egyéb".
  hu_non_established_rc_on_row_35: {
    headline:
      "Reverse-charge sales by a supplier not established in Hungary are "
      + "reported on row 35 (\u201cEgyéb\u201d), base only.",
    fix:
      "Nothing blocks filing. Row 04 covers domestic § 142 reverse charge "
      + "only, so these sales go on row 35 with the tax column empty — the "
      + "buyer accounts for the VAT. NAV's guide does not name this case on "
      + "row 35 expressly; confirm with your adviser if in doubt.",
    hint:
      "Non-established reverse-charge sales: row 35, base only — the buyer pays the VAT.",
  },
  rate_split_keys_match_known_categories: {
    headline:
      "One or more sales were taxed at a VAT rate this country's "
      + "form doesn't recognise.",
    fix:
      "Inspect the offending transactions below — if the rate is "
      + "bad source data, fix it at source and re-ingest. If it's "
      + "a genuine new jurisdiction rate, flag it so the rate "
      + "table maintainer can patch the repo.",
    // Short single-line variant for the chip-row breach hint.
    hint:
      "Sales were taxed at a VAT rate this country's form doesn't "
      + "recognise. See Offenders below.",
  },
  vp1_period_set: {
    headline:
      "We need to know whether this client files monthly or "
      + "quarterly before we can finish this return.",
    fix:
      "Pick a filing cadence below — we'll fill the right box "
      + "for you and remember the choice for future returns.",
    hint:
      "Filing cadence (monthly/quarterly) not set — pick one in "
      + "the cadence wizard.",
  },
};

export const RULE_COPY_NAMES = new Set(Object.keys(RULE_COPY));   // A2-234 G8 — the coverage page

export const getRuleCopy = (ruleName, fallbackDescription) => {
  const c = RULE_COPY[ruleName];
  if (c) return c;
  // Unknown rule — fall back to the backend description as the
  // hint. Never return undefined; the caller relies on a stable
  // shape.
  return {
    headline: fallbackDescription || "Action required.",
    fix: null,
    hint: fallbackDescription || "Open this return for details.",
  };
};

// ── Page-build error copy ────────────────────────────────────
//
// Each entry has:
//   parse(message)  — optional, extracts structured fields from
//                     the raw backend message (e.g. expected_pe,
//                     got_pe). Returns an object or null.
//   build(parsed, ctx) → { headline, fix, technicalDetails,
//                          actions }
//
// `actions` is a list of `{ id, label, kind, target }`:
//   - kind: "navigate" | "advisory"
//   - target: a relative path to nav() to (for "navigate"),
//             or null (for "advisory" — frontend shows it as
//             a labelled-only next-step).

const _re_period_dates =
  /must be (\d{4}-\d{2}-\d{2}).*?got (\d{4}-\d{2}-\d{2})/;

const PAGE_BUILD_ERROR_COPY = {
  MONTHLY_CADENCE_REQUIRES_FULL_QUARTER_PERIOD: {
    parse: (msg) => {
      const m = _re_period_dates.exec(msg || "");
      if (!m) return { expected_pe: null, got_pe: null };
      return { expected_pe: m[1], got_pe: m[2] };
    },
    build: (parsed, ctx) => {
      const expected = parsed?.expected_pe;
      const got = parsed?.got_pe;
      // v0.4.87 — the calc-period controls live on the Dashboard
      // (Step 02 CALCULATE), not on a standalone `/calc/{id}`
      // page. The v0.4.86 build incorrectly pointed the
      // "extend period" action at `/calc/{calcId}`, which is NOT
      // a registered React Router route — operators saw a blank
      // screen. Reverting to `/` (Dashboard) and rewording the
      // button so it sets the right expectation.
      const clientId = ctx?.clientId || null;
      const country = ctx?.country || "IT";
      return {
        headline:
          "This IT LIPE return needs a full calendar quarter (3 months).",
        fix:
          (got
            ? `Your calc ends ${got}. `
            : "Your calc doesn't cover a full quarter. ")
          + (expected
            ? `Extend it to ${expected} (the last day of the quarter's 3rd month) to file monthly LIPE for this quarter, `
            : "Extend it to the last day of the quarter's 3rd month, ")
          + "or switch this client to quarterly IT cadence.",
        technicalDetails:
          "Rule: MONTHLY_CADENCE_REQUIRES_FULL_QUARTER_PERIOD — "
          + "monthly LIPE always files a 3-month bundle aligned "
          + "to Jan-Mar / Apr-Jun / Jul-Sep / Oct-Dec. Partial-"
          + "quarter periods are refused loudly so we never "
          + "file best-effort monthly modules.",
        actions: [
          {
            id: "extend-period",
            label: expected
              ? `Open Dashboard — extend Period End to ${expected} →`
              : "Open Dashboard to extend the period →",
            kind: "navigate",
            target: "/",
          },
          // v0.4.86 — when the 409 detail carries a client_id,
          // the cadence-switch button calls updateClient inline
          // instead of dropping the operator onto /admin/clients
          // to hunt for the right row. When client_id is missing
          // (older calcs, dev fixtures), fall back to navigate.
          clientId
            ? {
                id: "switch-cadence",
                label: "Switch this client to quarterly IT cadence →",
                kind: "api-call",
                apiCall: {
                  type: "updateClient.localReturnFrequency",
                  clientId,
                  country,
                  cadence: "QUARTERLY",
                },
              }
            : {
                id: "switch-cadence",
                label: "Switch this client to quarterly IT cadence →",
                kind: "navigate",
                target: "/admin/clients",
              },
        ],
      };
    },
  },
};

export const getPageBuildErrorCopy = (code, message, ctx) => {
  if (!code) return null;
  const entry = PAGE_BUILD_ERROR_COPY[code];
  if (!entry) return null;
  const parsed = entry.parse ? entry.parse(message || "") : null;
  return entry.build(parsed, ctx || {});
};

// Test/introspection helper — lets the regression suite enumerate
// every registered code without re-exporting the internal map.
export const KNOWN_PAGE_BUILD_ERROR_CODES = Object.freeze(
  Object.keys(PAGE_BUILD_ERROR_COPY),
);
export const KNOWN_RULE_NAMES = Object.freeze(Object.keys(RULE_COPY));
